import type {
  PnidInstrument,
  PnidWizardState,
  TemplateAnnotation,
  TemplateAnnotationStatus,
  PnidInstrumentUpdate,
  TemplateAnnotationUpdate,
} from '../types';
import { hasManualInstrumentTag } from './instrumentIdentity';

function coerceTemplateStatus(status: string | null | undefined): TemplateAnnotationStatus {
  if (
    status === 'in_progress' ||
    status === 'final' ||
    status === 'final_and_reviewed' ||
    status === 'skipped'
  ) {
    return status;
  }
  return 'unverified';
}

export function isTemplateMatchInstrument(inst: PnidInstrument): boolean {
  return inst.provenance?.source === 'template_match';
}

export function isEquipmentContextInstrument(inst: PnidInstrument): boolean {
  return inst.context_type === 'equipment' && Boolean(inst.context_id);
}

export function isNodeContextInstrument(inst: PnidInstrument): boolean {
  return inst.context_type === 'node' && Boolean(inst.context_id);
}

export function isDerivedUntaggedInstrument(inst: PnidInstrument): boolean {
  return Boolean(inst.generated_id && !hasManualInstrumentTag(inst));
}

export type InstrumentEffectiveContext = { context_type: 'equipment' | 'node' | null; context_id: string | null };

export function getInstrumentEffectiveContext(
  inst: PnidInstrument,
  state: Pick<PnidWizardState, 'instruments'> | null | undefined,
): InstrumentEffectiveContext {
  if (inst.context_type === 'equipment' || inst.context_type === 'node') {
    return { context_type: inst.context_type, context_id: inst.context_id ?? null };
  }

  if (inst.context_type !== 'instrument' || !inst.context_id) {
    return { context_type: null, context_id: null };
  }

  const instrumentsById = new Map((state?.instruments ?? []).map((entry) => [entry.id, entry]));
  const seen = new Set<string>([inst.id]);
  let current: PnidInstrument | undefined = instrumentsById.get(inst.context_id);

  while (current) {
    if (seen.has(current.id)) {
      return { context_type: null, context_id: null };
    }
    seen.add(current.id);

    if (current.context_type === 'equipment' || current.context_type === 'node') {
      return { context_type: current.context_type, context_id: current.context_id ?? null };
    }

    if (current.context_type !== 'instrument' || !current.context_id) {
      return { context_type: null, context_id: null };
    }

    current = instrumentsById.get(current.context_id);
  }

  return { context_type: null, context_id: null };
}

export function getEquipmentContextInstruments(
  state: Pick<PnidWizardState, 'instruments'> | null | undefined,
  equipmentId?: string | null,
): PnidInstrument[] {
  const instruments = state?.instruments ?? [];
  return instruments.filter((inst) => {
    const effectiveContext = getInstrumentEffectiveContext(inst, state);
    if (effectiveContext.context_type !== 'equipment') return false;
    return equipmentId ? effectiveContext.context_id === equipmentId : true;
  });
}

/**
 * The instruments that hang off nothing: no equipment, no line, and no chain of
 * instrument contexts that reaches one. A P&ID with no equipment and no lines can only
 * carry these, so they are a layer of their own rather than a state of a parent's list.
 *
 * Template matches are left out: they already reach the canvas through
 * `getTemplateAnnotationsFromInstruments`, which draws attached and unattached alike.
 */
export function getUnattachedInstruments(
  state: Pick<PnidWizardState, 'instruments'> | null | undefined,
): PnidInstrument[] {
  const instruments = state?.instruments ?? [];
  return instruments.filter(
    (inst) =>
      !isTemplateMatchInstrument(inst) &&
      getInstrumentEffectiveContext(inst, state).context_type === null,
  );
}

export function getNodeContextInstruments(
  state: Pick<PnidWizardState, 'instruments'> | null | undefined,
  nodeId?: string | null,
): PnidInstrument[] {
  const instruments = state?.instruments ?? [];
  return instruments
    .filter((inst) => {
      const effectiveContext = getInstrumentEffectiveContext(inst, state);
      if (effectiveContext.context_type !== 'node') return false;
      return nodeId ? effectiveContext.context_id === nodeId : true;
    })
    .sort((a, b) => {
      const aOrder = a.sequence_order ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.sequence_order ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.id.localeCompare(b.id);
    });
}

export function getDirectNodeContextInstruments(
  state: Pick<PnidWizardState, 'instruments'> | null | undefined,
  nodeId?: string | null,
): PnidInstrument[] {
  const instruments = state?.instruments ?? [];
  return instruments
    .filter((inst) => {
      if (!isNodeContextInstrument(inst)) return false;
      return nodeId ? inst.context_id === nodeId : true;
    })
    .sort((a, b) => {
      const aOrder = a.sequence_order ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.sequence_order ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.id.localeCompare(b.id);
    });
}

export function getTemplateAnnotationGeometry(inst: PnidInstrument) {
  const meta = inst.provenance?.metadata ?? {};
  const templateBox = meta.template_bbox ?? {};
  const bbox = inst.bbox;

  return {
    x: Number(templateBox.x ?? bbox?.x1 ?? 0),
    y: Number(templateBox.y ?? bbox?.y1 ?? 0),
    width: Number(templateBox.width ?? ((bbox?.x2 ?? 0) - (bbox?.x1 ?? 0))),
    height: Number(templateBox.height ?? ((bbox?.y2 ?? 0) - (bbox?.y1 ?? 0))),
  };
}

export function toTemplateAnnotation(
  inst: PnidInstrument,
  state?: Pick<PnidWizardState, 'instruments'> | null,
): TemplateAnnotation {
  const meta = inst.provenance?.metadata ?? {};
  const geometry = getTemplateAnnotationGeometry(inst);
  const effectiveContext = getInstrumentEffectiveContext(inst, state ?? { instruments: [inst] });

  return {
    id: inst.id,
    template_id: String(meta.template_id ?? ''),
    template_name: String(meta.template_name ?? ''),
    template_category: meta.template_category ?? null,
    dpi: Number(meta.dpi ?? 0),
    x: geometry.x,
    y: geometry.y,
    width: geometry.width,
    height: geometry.height,
    confidence: Number(meta.confidence ?? 0),
    chamfer_distance: Number(meta.chamfer_distance ?? 0),
    created_at: inst.created_at ?? new Date(0).toISOString(),
    status: coerceTemplateStatus(inst.status),
    instrument_text: inst.instrument_text,
    instrument_type: inst.instrument_type,
    subtype: inst.subtype,
    contextual_type: inst.contextual_type,
    isolation_valve_tags: inst.isolation_valve_tags ?? [],
    linked_instruments: inst.linked_instruments ?? [],
    notes: inst.notes,
    attached_context_type: effectiveContext.context_type,
    attached_context_id: effectiveContext.context_id,
  };
}

export function getTemplateAnnotationsFromInstruments(
  instruments: PnidInstrument[] | null | undefined,
): TemplateAnnotation[] {
  const state = { instruments: instruments ?? [] };
  return (instruments ?? [])
    .filter(isTemplateMatchInstrument)
    .map((inst) => toTemplateAnnotation(inst, state))
    .sort((a, b) => a.id.localeCompare(b.id));
}

export function buildTemplateAnnotationUpdate(
  update: TemplateAnnotationUpdate,
): PnidInstrumentUpdate {
  const next: PnidInstrumentUpdate = {};

  if ('status' in update) next.status = update.status;
  // A template annotation is one rectangle, so its edit is a one-region list.
  if ('bbox' in update) next.bboxes = update.bbox ? [update.bbox] : [];
  if ('instrument_text' in update) next.instrument_text = update.instrument_text ?? null;
  if ('instrument_type' in update) next.instrument_type = update.instrument_type ?? null;
  if ('subtype' in update) next.subtype = update.subtype ?? null;
  if ('contextual_type' in update) {
    next.contextual_type = update.contextual_type ?? null;
    next.core_fields = {
      ...(next.core_fields ?? {}),
      contextual_type: update.contextual_type ?? null,
    };
  }
  if ('isolation_valve_tags' in update) next.isolation_valve_tags = update.isolation_valve_tags ?? [];
  if ('linked_instruments' in update) next.linked_instruments = update.linked_instruments ?? [];
  if ('notes' in update) next.notes = update.notes ?? null;
  if ('attached_context_type' in update) next.context_type = update.attached_context_type ?? null;
  if ('attached_context_id' in update) next.context_id = update.attached_context_id ?? null;

  return next;
}
